import { useTranslation } from '../../../core/hooks/useTranslation';
import { formatNumberWithCommas } from '../../../core/utils/formatters';
import { Bookmark, Star, Coins, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

export interface PaymentPlan {
    id: string;
    planCategory: 'ORIGINAL_PLAN' | 'OFFER_PLAN';
    concurrentInstallment: number;
    installmentFrequency: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
    totalYears: number;
    downPayment: number;
    handoverPayment: number;
}

interface SavingsCalculatorProps {
    unitPrice: string | number;
    paymentPlans?: PaymentPlan[];
    legacyPaymentText?: string;
    legacyPaymentDuration?: string | number;
}

export const SavingsCalculator = ({
    unitPrice,
    paymentPlans = [],
    legacyPaymentText,
    legacyPaymentDuration
}: SavingsCalculatorProps) => {
    const { t, isRTL } = useTranslation();
    const [showAllPlans, setShowAllPlans] = useState(false);

    const originalPlans = paymentPlans.filter(p => p.planCategory === 'ORIGINAL_PLAN');
    const offerPlans = paymentPlans.filter(p => p.planCategory === 'OFFER_PLAN');

    const hasPlans = originalPlans.length > 0 || offerPlans.length > 0;

    if (!hasPlans) return null;

    // Display offer plans first, then original plans
    const displayPlans = [...offerPlans, ...originalPlans];

    // Determine plans to show based on mobile toggle state (1 by default on mobile)
    const visiblePlansOnMobile = showAllPlans ? displayPlans : displayPlans.slice(0, 1);
    const hasMorePlans = displayPlans.length > 1;

    // Helper functions
    const getMultiplier = (freq: string) => {
        if (freq === 'MONTHLY') return 12;
        if (freq === 'QUARTERLY') return 4;
        return 1;
    };

    const calculateTotal = (plan: PaymentPlan) => {
        const installmentsTotal = plan.concurrentInstallment * (plan.totalYears * getMultiplier(plan.installmentFrequency));
        return plan.downPayment + plan.handoverPayment + installmentsTotal;
    };

    // Calculate potential savings 
    const originalPlan = originalPlans[0];
    const offerPlan = offerPlans[0];
    const originalTotal = originalPlan ? calculateTotal(originalPlan) : 0;
    const offerTotal = offerPlan ? calculateTotal(offerPlan) : 0;
    const savings = originalPlan && offerPlan ? Math.max(0, originalTotal - offerTotal) : 0;

    const renderPlanCard = (plan: PaymentPlan) => {
        const isOffer = plan.planCategory === 'OFFER_PLAN';

        return (
            <div
                key={plan.id}
                className="bg-white rounded-[20px] p-5 md:p-6 shadow-[0px_2px_8px_rgba(0,0,0,0.03)] border border-slate-50 transition-all duration-300 relative group overflow-hidden flex-shrink-0 w-full md:w-[340px] lg:w-[380px]"
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                <div className="flex justify-between items-center gap-3 h-full">
                    <div className="flex flex-col items-start gap-1">
                        {/* Top Label */}
                        <div className="flex items-center gap-1.5 mb-2">
                            {isOffer ? (
                                <>
                                    <Star className="w-[18px] h-[18px] text-[#F59E0B] fill-current" />
                                    <span className="text-[#F59E0B] font-bold text-[14px]">{t('unit.specialOffer', 'Special Offer')}</span>
                                </>
                            ) : (
                                <>
                                    <Bookmark className="w-[18px] h-[18px] text-[#0A66C2] fill-current" />
                                    <span className="text-[#0A66C2] font-bold text-[14px]">{t('unit.standardPlan', 'Original Plan')}</span>
                                </>
                            )}
                        </div>

                        {/* Installment amount */}
                        <div className="flex flex-col items-start gap-0.5">
                            <div className="flex items-baseline flex-wrap gap-1.5" dir="ltr">
                                <span className="text-[28px] lg:text-3xl font-display font-extrabold text-[#1F2937] tracking-tight leading-none">{formatNumberWithCommas(plan.concurrentInstallment)}</span>
                            </div>
                            <span className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">
                                EGP {t(`unit.installment_${plan.installmentFrequency.toLowerCase()}`, plan.installmentFrequency)}
                            </span>
                        </div>

                        {/* Down payment text - Encapsulated LTR numbers alongside natural RTL text */}
                        <div className="text-[12px] text-slate-500 font-medium flex flex-row flex-wrap gap-x-4 gap-y-1 items-center mt-3">
                            <div className="flex items-center flex-wrap gap-1.5" dir={isRTL ? "rtl" : "ltr"}>
                                <span className="opacity-60 text-[10px]">•</span>
                                <div className="flex items-baseline gap-1" dir="ltr">
                                    <span className="font-bold text-[#4B5563]">{formatNumberWithCommas(plan.downPayment)}</span>
                                    <span className="text-[10px]">EGP</span>
                                </div>
                                <span>{t('unit.downPayment', 'Down Payment')}</span>
                            </div>

                            {plan.handoverPayment > 0 && (
                                <div className="flex items-center flex-wrap gap-1.5" dir={isRTL ? "rtl" : "ltr"}>
                                    <span className="opacity-60 text-[10px]">•</span>
                                    <div className="flex items-baseline gap-1" dir="ltr">
                                        <span className="font-bold text-[#4B5563]">{formatNumberWithCommas(plan.handoverPayment)}</span>
                                        <span className="text-[10px]">EGP</span>
                                    </div>
                                    <span>{t('unit.handoverPayment', 'Handover')}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Duration */}
                    <div className="flex flex-col items-end justify-center gap-3 shrink-0 h-full ml-auto">
                        <span className="font-bold text-[#1F2937] text-[14px]" dir={isRTL ? 'rtl' : 'ltr'}>
                            {plan.totalYears} {t('common.years', 'Years')}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="bg-slate-50/50 rounded-[28px] p-4 md:p-6 md:px-8 shadow-[0px_4px_24px_rgba(0,0,0,0.02)] flex flex-col gap-4 md:gap-6 w-full max-w-[100vw] overflow-hidden relative">
            {/* Header Area */}
            <div className="flex flex-col gap-1 items-center justify-center text-center mt-2 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
                <h3 className="text-[22px] md:text-2xl font-display font-bold text-secondary">{t('unit.paymentPlans', 'Payment Plan')}</h3>

                {/* Total Price Banner */}
                <div className="flex items-center gap-1.5 justify-center opacity-80">
                    <span className="text-secondary text-[14px] font-bold">{t('unit.totalPrice', 'Total Price')}:</span>
                    <div className="flex items-baseline gap-1" dir="ltr">
                        <span className="text-[20px] md:text-xl font-display font-extrabold text-secondary tracking-tight">{formatNumberWithCommas(unitPrice)}</span>
                        <span className="text-[12px] font-bold text-slate-500">EGP</span>
                    </div>
                </div>
            </div>

            {/* Savings Banner Highlight */}
            {savings > 0 && (
                <div className="bg-gradient-to-br from-[#F59E0B]/10 to-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-[20px] p-4 flex flex-row items-center justify-between gap-4 mx-2 md:max-w-max md:mx-auto md:px-6" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-white text-[#F59E0B] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm shadow-[#F59E0B]/20">
                            <Coins className="w-5 h-5" />
                        </div>
                        <p className="text-[#F59E0B] font-bold text-sm md:pr-4">{t('unit.youSave', 'You Save With Special Offer!')}</p>
                    </div>
                    <div className="flex items-baseline gap-1 shrink-0" dir="ltr">
                        <span className="text-xl font-display font-bold text-[#F59E0B] tracking-tight">{formatNumberWithCommas(savings)}</span>
                        <span className="text-[11px] font-bold text-[#F59E0B]/70">EGP</span>
                    </div>
                </div>
            )}

            {/* Plans List Container */}
            {hasPlans ? (
                <div className="relative w-full">
                    {/* Mobile View: Vertical list with toggle */}
                    <div className="flex flex-col gap-4 md:hidden">
                        {visiblePlansOnMobile.map(renderPlanCard)}

                        {/* Toggle Button for Mobile */}
                        {hasMorePlans && (
                            <button
                                onClick={() => setShowAllPlans(!showAllPlans)}
                                className="mt-2 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold text-sm transition-colors hover:bg-slate-50 active:bg-slate-100"
                            >
                                {showAllPlans ? (
                                    <>
                                        <span>{t('common.seeLess', 'See Less')}</span>
                                        <ChevronUp className="w-4 h-4" />
                                    </>
                                ) : (
                                    <>
                                        <span>{t('unit.seeMorePlans', 'See More Plans')} ({displayPlans.length - 1})</span>
                                        <ChevronDown className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Desktop View: Horizontal scrollable row */}
                    <div className="hidden md:flex flex-row overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {displayPlans.map((plan) => (
                            <div key={`desktop-${plan.id}`} className="snap-center shrink-0">
                                {renderPlanCard(plan)}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                legacyPaymentText && (
                    <div className="bg-white rounded-[20px] p-5 md:p-6 shadow-[0px_2px_8px_rgba(0,0,0,0.03)] border border-slate-50 relative group overflow-hidden md:max-w-md md:mx-auto w-full" dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className="flex justify-between items-center gap-3 h-full">
                            <div className="flex flex-col items-start gap-1">
                                <div className="flex items-center gap-1.5 mb-2 justify-start">
                                    <Bookmark className="w-[18px] h-[18px] text-[#0A66C2] fill-current" />
                                    <span className="text-[#0A66C2] font-bold text-[14px]">{t('unit.standardPlan', 'Original Plan')}</span>
                                </div>

                                <div className="flex flex-col items-start gap-0.5">
                                    <div className="flex items-baseline flex-wrap gap-1.5" dir="ltr">
                                        <span className="text-[28px] md:text-3xl font-display font-extrabold text-[#1F2937] tracking-tight leading-none">{formatNumberWithCommas(Number(legacyPaymentText?.replace(/[^0-9.]/g, '')) || legacyPaymentText)}</span>
                                    </div>
                                    <span className="text-[13px] font-bold text-slate-500 uppercase tracking-wide">EGP {t('unit.monthly', 'Installment')}</span>
                                </div>
                            </div>

                            {legacyPaymentDuration && (
                                <div className="flex flex-col items-end justify-center gap-3 shrink-0 h-full ml-auto">
                                    <span className="font-bold text-[#1F2937] text-[14px]" dir={isRTL ? 'rtl' : 'ltr'}>
                                        {legacyPaymentDuration} {t('common.years', 'Years')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )
            )}
        </section>
    );
};
